import $ from 'jquery'
import { GeoJSON } from 'ol/format'
import Layer from 'ol/layer/Layer'
import Feature from 'ol/Feature'
/**
 * @module
 * @api
 */

/**
 * Feature API class
 * @param {Object} config
 * @constructor
 */
export default function Geometry(config) {
  $.extend(this, config || {})
}

/**
 * Export geometries as GeoJSON
 * @param {string=} layerName Name of the layer to export as GeoJSON. If no layer names is defined the default drawlayer will be exported.
 * @return {Object} GeoJSON
 * @memberof! module:api/Geometry#
 * @api
 */
Geometry.prototype.export = function(layerName) {
  layerName = layerName || 'draw'
  var layer = this.layer.getLayerByName(layerName)
  var features = layer.getSource().getFeatures()
  var parser = new GeoJSON()
  var geoJSON = parser.writeFeaturesObject(features)
  return geoJSON
}

/**
 * Add geometries to map. If layerName is set a new layer with that name will be created.
 * If no name is speciefied the default name will be used.
 * Only layers with specified name will be shown in the TOC layer control.
 *
 * The style parameter can dynamically map a text label, color, width or radius to a property of the
 * feature to be styled using an object like in this example:
 * ```json
 * {
 *   stroke: {
 *     color: {
 *       property: 'Status',
 *       mapping : {
 *         '0': 'rgba(255, 0, 0, 1)',
 *         '1': 'rgba(0, 0, 255, 1)'
 *       }
 *     },
 *     width: 4
 *   },
 *   text: {
 *     property: 'Label',
 *     minResolution: 4,
 *     maxResolution: 8,
 *     overflow: false,
 *     fill: {
 *       color: {
 *         property: 'Status',
 *         mapping : {
 *           '0': 'rgba(255, 0, 0, 1)',
 *           '1': 'rgba(0, 0, 255, 1)'
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * @param {string} geoJSON
 * @param {string=} layerName
 * @param {object=} style
 * @memberof! module:api/Geometry#
 * @api
 */
Geometry.prototype.add = function(geoJSON, layerName, style) {
  layerName = layerName || this.layer.getDefaultLayerName()

  var layer = this.layer.getLayerByName(layerName)

  if (!layer) {
    layer = this.layer.addLayer(layerName, style)
    if (layerName !== this.defaultLayerName) {
      this.layer.addLayerToToc(layer, layerName)
    }
  }

  this.addGeoJSON(layer, geoJSON)

  return layer
}

/**
 * Add GeoJSON features to layer
 * @param {Feature} feature
 * @param {string} geoJSON
 */
Geometry.prototype.addGeoJSON = function(layer, geoJSON) {
  var format = new GeoJSON()
  var source = layer.getSource()
  var features = format.readFeatures(geoJSON.object)
  features.forEach(f => (f.layer_ = layer))
  source.addFeatures(features)
}

/**
 * Get all layers created via API.
 * @deprecated Since version 1.6.0. You should now use api.layer.getLayers().
 * @return {Object} Layers
 * @memberof! module:api/Geometry#
 * @api
 */
Geometry.prototype.getLayers = function() {
  return this.layer.getLayers()
}

/**
 * Clear all features from specified layer name. If no name is speciefied the default name will be used.
 * @param {string=} layerName
 * @memberof! module:api/Geometry#
 * @api
 */
Geometry.prototype.clear = function(layerName) {
  var targetLayerName = layerName || this.defaultLayerName
  var layer = this.layer.getLayerByName(targetLayerName)
  if (layer) {
    var source = layer.getSource()
    source.clear()
  }
}

/**
 * Remove from map and from TOC. If no name is speciefied the default name will be used.
 * @param {string=} layerName
 * @memberof! module:api/Geometry#
 * @api
 */
Geometry.prototype.remove = function(layerName) {
  var targetLayerName = layerName || this.defaultLayerName
  this.layer.removeLayer(targetLayerName)
}

/**
 * Get features by property
 * @param {Layer} layer
 * @param {string} property
 * @param {string} value
 * @return {array} features
 * @api
 */
Geometry.prototype.getFeatureByProperty = function(layer, property, value) {
  var vectorSource, featuresFound

  featuresFound = []

  if (layer) {
    vectorSource = layer.getSource()

    vectorSource.forEachFeature(
      function(feature) {
        if (this.featurePropertyMatch(feature, property, value)) {
          featuresFound.push(feature)
        }
      }.bind(this)
    )
  }

  return featuresFound
}

/**
 * Check feature has a property
 * @param {Feature} feature
 * @param {string|string[]} property name or an array of properties
 * @param {string|string[]} value or an array of values
 * @return {Feature}  feature
 */
Geometry.prototype.featurePropertyMatch = function(feature, property, value) {
  if (property.constructor.toString().indexOf('Array') !== -1) {
    for (var i = 0; i < property.length; i++) {
      if (feature.get(property[i]) !== value[i]) {
        return false
      }
    }
    return true
  } else {
    return feature.get(property) === value
  }
}
